import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
  useTypeahead,
} from '@floating-ui/react';
import type { Placement } from '@floating-ui/react';
import { useRef, useState } from 'react';

export type FloatingUIWidth = 'trigger' | 'auto' | 'none';

export interface UseFloatingUIOptions {
  // === Core options ===
  /** Initial open state */
  initialOpen?: boolean | undefined;
  /** Controlled open state */
  open?: boolean | undefined;
  /** Callback when open state changes */
  onOpenChange?: ((open: boolean) => void) | undefined;
  /** Placement of the floating element */
  placement?: Placement | undefined;
  /** Offset from the trigger in pixels */
  offsetPx?: number | undefined;

  // === Interaction options ===
  /** Whether click interaction is enabled */
  clickEnabled?: boolean | undefined;
  /** Whether clicking outside closes the floating element */
  closeOnClickOutside?: boolean | undefined;
  /** Whether pressing Escape closes the floating element */
  closeOnEscape?: boolean | undefined;

  // === List features (for Select/Dropdown) ===
  /** Enable list navigation with arrow keys */
  enableListNavigation?: boolean | undefined;
  /** Enable typeahead search */
  enableTypeahead?: boolean | undefined;
  /** Labels for typeahead (usually option labels) */
  typeaheadLabels?: string[] | undefined;
  /** Currently selected index (for list navigation) */
  selectedIndex?: number | undefined;

  // === Size constraints ===
  /** Width behavior: 'trigger' = match trigger width, 'auto' = fit content with max-width, 'none' = no constraint */
  width?: FloatingUIWidth | undefined;
  /** Min width in pixels */
  minWidth?: number | undefined;
  /** Max width in pixels (only used when width='auto') */
  maxWidth?: number | undefined;
  /** Max height in pixels */
  maxHeight?: number | undefined;

  // === ARIA ===
  /** ARIA role for the floating element */
  role?: 'listbox' | 'menu' | 'dialog' | 'tooltip' | undefined;
}

export interface UseFloatingUIReturn {
  /** Whether the floating element is open */
  isOpen: boolean;
  /** Set the open state */
  setIsOpen: (open: boolean) => void;
  /** Currently active (focused) index - only for list navigation */
  activeIndex: number | null;
  /** Set the active index */
  setActiveIndex: (index: number | null) => void;
  /** Ref setter for the reference element */
  setReference: (node: HTMLElement | null) => void;
  /** Ref setter for the floating element */
  setFloating: (node: HTMLElement | null) => void;
  /** Styles for the floating element */
  floatingStyles: React.CSSProperties;
  /** Floating-ui context */
  context: ReturnType<typeof useFloating>['context'];
  /** Props for the reference element */
  getReferenceProps: ReturnType<typeof useInteractions>['getReferenceProps'];
  /** Props for the floating element */
  getFloatingProps: ReturnType<typeof useInteractions>['getFloatingProps'];
  /** Props for list items - only for list navigation */
  getItemProps: ReturnType<typeof useInteractions>['getItemProps'];
  /** Ref array for list items - only for list navigation */
  listRef: React.RefObject<(HTMLElement | null)[]>;
}

/**
 * Unified hook for all floating UI components (Select, Dropdown, Popover, Tooltip)
 *
 * @example
 * ```tsx
 * // Simple popover
 * const { isOpen, setReference, setFloating, ... } = useFloatingUI({
 *   placement: 'bottom-start',
 * });
 *
 * // Select with list navigation
 * const { isOpen, activeIndex, listRef, ... } = useFloatingUI({
 *   enableListNavigation: true,
 *   enableTypeahead: true,
 *   typeaheadLabels: options.map(o => o.label),
 *   selectedIndex: 0,
 *   width: 'trigger',
 * });
 * ```
 */
export function useFloatingUI({
  // Core
  initialOpen = false,
  open: controlledOpen,
  onOpenChange,
  placement = 'bottom-start',
  offsetPx = 4,
  // Interactions
  clickEnabled = true,
  closeOnClickOutside = true,
  closeOnEscape = true,
  // List features
  enableListNavigation = false,
  enableTypeahead = false,
  typeaheadLabels = [],
  selectedIndex = -1,
  // Size
  width = 'none',
  minWidth,
  maxWidth,
  maxHeight,
  // ARIA
  role: ariaRole = 'dialog',
}: UseFloatingUIOptions = {}): UseFloatingUIReturn {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  // Support both controlled and uncontrolled modes
  const isOpen = controlledOpen ?? uncontrolledOpen;
  const setIsOpen = (newOpen: boolean) => {
    setUncontrolledOpen(newOpen);
    onOpenChange?.(newOpen);
  };

  // Refs for list navigation and typeahead
  const listRef = useRef<(HTMLElement | null)[]>([]);
  const listContentRef = useRef<string[]>(typeaheadLabels);
  listContentRef.current = typeaheadLabels;

  // Build middleware array based on options
  const middleware = [offset(offsetPx), flip({ padding: 8 }), shift({ padding: 8 })];

  // Add size middleware if width or height constraints are needed
  if (width !== 'none' || minWidth || maxWidth || maxHeight) {
    middleware.push(
      size({
        apply({ rects, elements, availableHeight }) {
          const styles: Record<string, string> = {};

          if (width === 'trigger') {
            styles.width = `${rects.reference.width}px`;
          } else if (width === 'auto') {
            styles.width = 'max-content';
          }

          if (minWidth) {
            styles.minWidth = `${minWidth}px`;
          }

          if (maxWidth) {
            styles.maxWidth = `${maxWidth}px`;
          }

          if (maxHeight) {
            styles.maxHeight = `${Math.min(availableHeight, maxHeight)}px`;
          }

          Object.assign(elements.floating.style, styles);
        },
        padding: 8,
      })
    );
  }

  const {
    refs,
    floatingStyles: baseFloatingStyles,
    context,
  } = useFloating({
    placement,
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    middleware,
  });

  // Merge size constraints into floatingStyles to ensure they persist across React re-renders
  const floatingStyles: React.CSSProperties = {
    ...baseFloatingStyles,
    ...(minWidth ? { minWidth: `${minWidth}px` } : {}),
    ...(maxWidth ? { maxWidth: `${maxWidth}px` } : {}),
  };

  // Build interactions array
  const interactions = [
    useClick(context, { enabled: clickEnabled }),
    useDismiss(context, {
      outsidePress: closeOnClickOutside,
      escapeKey: closeOnEscape,
    }),
    useRole(context, { role: ariaRole }),
  ];

  // Conditionally add list navigation
  if (enableListNavigation) {
    interactions.push(
      useListNavigation(context, {
        listRef,
        activeIndex,
        selectedIndex: selectedIndex >= 0 ? selectedIndex : null,
        onNavigate: setActiveIndex,
        loop: true,
      })
    );
  }

  // Conditionally add typeahead
  if (enableTypeahead) {
    interactions.push(
      useTypeahead(context, {
        listRef: listContentRef,
        activeIndex,
        selectedIndex: selectedIndex >= 0 ? selectedIndex : null,
        onMatch: (index) => {
          if (isOpen) {
            setActiveIndex(index);
          }
        },
      })
    );
  }

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(interactions);

  return {
    isOpen,
    setIsOpen,
    activeIndex,
    setActiveIndex,
    setReference: refs.setReference,
    setFloating: refs.setFloating,
    floatingStyles,
    context,
    getReferenceProps,
    getFloatingProps,
    getItemProps,
    listRef,
  };
}
